import { Trans } from "@lingui/react/macro";
import { type ReactNode, useRef } from "react";

import { cn } from "@/utils/shadcn-ui";

import type { DataTableInstance } from "../table-features";
import type { FilterField } from "./types";

import { toggleableColumns } from "../column-visibility";
import { useScrollEdges } from "../use-scroll-shadows";
import { FilterDialogPopover } from "./filter-dialog-popover";
import { getActiveCount } from "./tanstack-bridge";

export function FilterToolbar<T>({
  table,
  fields,
  noun,
  actions,
  isLoading,
}: {
  table: DataTableInstance<T>;
  fields: FilterField<T>[];
  /** Noun for the auto-generated "X of Y" count text, pluralized for a count. */
  noun?: (count: number) => string;
  actions?: ReactNode;
  isLoading?: boolean;
}) {
  const activeCount = getActiveCount(table, fields);
  // Top-level rows only — sub-rows aren't items of their own. Under server
  // pagination the loaded rows are one page, so the server's total wins.
  const totalCount =
    table.options.rowCount ?? table.getCoreRowModel().rows.length;
  const shownCount =
    activeCount > 0 ? table.getFilteredRowModel().rows.length : totalCount;
  // Walked once here and handed to the dialog: it is both what decides whether
  // the button earns its place and what the Columns tab lists.
  const columns = toggleableColumns(table);
  // The dialog also hosts the Columns tab, so it earns its place as soon as
  // there is either something to filter or something to show/hide.
  const hasDialogContent = fields.length > 0 || columns.length > 0;

  return (
    <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between sm:min-h-9">
      {actions && <ActionsRow>{actions}</ActionsRow>}

      <div className="flex items-center justify-between gap-3 sm:justify-end sm:shrink-0 sm:ml-auto">
        {noun && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {isLoading ? (
              <>
                {/* No count to agree with yet, so ask for the form a plain
                    plural takes ("patients", "pacientov"). */}
                <strong className="text-foreground">–</strong> {noun(0)}
              </>
            ) : activeCount > 0 ? (
              <Trans>
                <strong className="text-foreground">{shownCount}</strong> of{" "}
                <strong className="text-foreground">{totalCount}</strong>{" "}
                {noun(totalCount)}
              </Trans>
            ) : (
              <>
                <strong className="text-foreground">{totalCount}</strong>{" "}
                {noun(totalCount)}
              </>
            )}
          </span>
        )}
        {hasDialogContent && (
          <FilterDialogPopover
            table={table}
            fields={fields}
            columns={columns}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The toolbar's actions, on one row beside the count and Filter button: when
 * they don't fit they scroll sideways rather than wrap onto a line of their own
 * above the table. A shadow on the side hiding more of them says so, the same
 * strip the table draws beside its pinned columns (`table.module.css`). The
 * vertical padding keeps focus rings clear of the scroll clip. On a phone the
 * actions stack instead, so none of it applies there.
 */
function ActionsRow({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  useScrollEdges(scrollerRef);

  return (
    <div className={cn("relative sm:min-w-0", SCROLL_SHADOWS)}>
      <div
        ref={scrollerRef}
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:overflow-x-auto sm:py-1 sm:*:shrink-0"
      >
        {children}
      </div>
    </div>
  );
}

const SCROLL_SHADOWS = cn(
  "sm:before:pointer-events-none sm:before:absolute sm:before:inset-y-0 sm:before:left-0 sm:before:z-10 sm:before:w-1 sm:before:bg-linear-to-r sm:before:from-border sm:before:to-transparent sm:before:opacity-0 sm:data-scrolled:before:opacity-100",
  "sm:after:pointer-events-none sm:after:absolute sm:after:inset-y-0 sm:after:right-0 sm:after:z-10 sm:after:w-1 sm:after:bg-linear-to-l sm:after:from-border sm:after:to-transparent sm:after:opacity-0 sm:data-scrolled-end:after:opacity-100",
);
